import React, { useEffect, useMemo, useState } from 'react';
import { simboloMoneda } from '../services/negocioActual';
import { obtenerPedidosParaEstadisticas } from '../services/pedidosService';
import { mensajeAmigable } from '../utils/errores';
import { calcularEstadisticas, pedidosPorSemana, desdePeriodo, hastaPeriodo, textoTiempo, RESULTADOS, SIN_RESULTADO } from '../utils/pedidosResultado';

const ACENTO = 'var(--color-primario,#DB2777)';
const TINTA = '#111827';
const SUAVE = '#6B7280';
const LINEA = '#F1EEF8';

const dinero = (n) => `${simboloMoneda()} ${Number(n || 0).toLocaleString('es-HN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const PERIODOS = [
  ['mes', 'Este mes'], ['mes_pasado', 'Mes pasado'], ['30', 'Últimos 30 días'], ['90', 'Últimos 90 días'], ['todo', 'Todo'],
];

const Tile = ({ lbl, num, sub, color = TINTA }) => (
  <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '14px', border: `1px solid ${LINEA}` }}>
    <p style={{ margin: '0 0 4px', fontSize: '12px', color: SUAVE, fontWeight: 600 }}>{lbl}</p>
    <p style={{ margin: 0, fontSize: '22px', fontWeight: 800, color }}>{num}</p>
    {sub && <p style={{ margin: '4px 0 0', fontSize: '12px', color: SUAVE }}>{sub}</p>}
  </div>
);

const Seccion = ({ titulo, sub, children }) => (
  <div className="k-card" style={{ padding: '16px', marginBottom: '12px' }}>
    <p style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: TINTA }}>{titulo}</p>
    {sub && <p style={{ margin: '2px 0 10px', fontSize: '12px', color: SUAVE }}>{sub}</p>}
    {children}
  </div>
);

// Barra horizontal: una sola medida, con el número escrito al lado.
const Barra = ({ valor, max, color = ACENTO }) => (
  <div style={{ flex: 1, height: '10px', backgroundColor: '#F4F1FA', borderRadius: '999px', overflow: 'hidden', minWidth: '60px' }}>
    <div style={{ width: `${max ? (valor / max) * 100 : 0}%`, height: '100%', backgroundColor: color, borderRadius: '999px' }} />
  </div>
);

// Estadísticas de los pedidos del catálogo: cuántos llegan, cuántos compran,
// de dónde vienen y por qué no compran.
export default function PedidosEstadisticas({ refrescar }) {
  const [periodo, setPeriodo] = useState('mes');
  const [todos, setTodos] = useState(null); // últimos ~2 años, se filtra aquí
  const [error, setError] = useState(null);
  const [semanaActiva, setSemanaActiva] = useState(null);

  useEffect(() => {
    let activo = true;
    setTodos(null); setError(null);
    const desde = new Date(); desde.setFullYear(desde.getFullYear() - 2);
    obtenerPedidosParaEstadisticas(desde.toISOString())
      .then((f) => activo && setTodos(f))
      .catch((e) => activo && setError(mensajeAmigable(e)));
    return () => { activo = false; };
  }, [refrescar]);

  const delPeriodo = useMemo(() => {
    if (!todos) return [];
    const d = desdePeriodo(periodo); const h = hastaPeriodo(periodo);
    return todos.filter((p) => { const f = new Date(p.creado_en); return (!d || f >= d) && (!h || f < h); });
  }, [todos, periodo]);
  const est = useMemo(() => calcularEstadisticas(delPeriodo), [delPeriodo]);
  const semanas = useMemo(() => (todos ? pedidosPorSemana(todos, 8) : []), [todos]);

  if (error) return <div style={{ padding: '16px', backgroundColor: '#FEE2E2', borderRadius: '12px', color: '#991B1B', fontWeight: 600 }}>⚠️ {error}</div>;
  if (!todos) return <div style={{ textAlign: 'center', padding: '40px', color: SUAVE }}>⏳ Calculando estadísticas...</div>;

  const atendidosConResultado = est.compro + est.seguimiento + est.no_compro;
  const maxOrigen = Math.max(1, ...est.porOrigen.map((o) => o.pedidos));
  const maxMotivo = Math.max(1, ...est.motivos.map((m) => m.n));
  const maxSemana = Math.max(1, ...semanas.map((s) => s.pedidos));
  const sa = semanaActiva != null ? semanas[semanaActiva] : null;
  const fechaCorta = (d) => d.toLocaleDateString('es-HN', { day: 'numeric', month: 'short' });

  // Embudo: recibidos → atendidos → compraron.
  const embudo = [
    { lbl: 'Pedidos recibidos', n: est.recibidos },
    { lbl: 'Atendidos', n: est.recibidos - est.pendientes },
    { lbl: 'Compraron', n: est.compro },
  ];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
        <span style={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>Período:</span>
        <select value={periodo} onChange={(e) => setPeriodo(e.target.value)} aria-label="Período de las estadísticas"
          style={{ padding: '8px 10px', borderRadius: '8px', border: '1px solid #D1D5DB', fontSize: '13px', backgroundColor: 'white' }}>
          {PERIODOS.map(([k, t]) => <option key={k} value={k}>{t}</option>)}
        </select>
      </div>

      {est.recibidos === 0 ? (
        <div style={{ textAlign: 'center', padding: '32px', color: '#9CA3AF' }}>No hay pedidos en este período. 📭</div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px', marginBottom: '12px' }}>
            <Tile lbl="🛒 Pedidos recibidos" num={est.recibidos} sub={`${est.pendientes} sin atender`} />
            <Tile lbl="🛍️ Compraron" num={est.compro} sub={`${est.conversion}% de los pedidos`} color="#047857" />
            <Tile lbl="⏳ En seguimiento" num={est.seguimiento} sub="Esperando respuesta" color="#B45309" />
            <Tile lbl="❌ No compraron" num={est.no_compro} sub={est.sinResultado ? `${est.sinResultado} sin resultado anotado` : 'Dales seguimiento'} color="#B91C1C" />
            <Tile lbl="💰 Vendido por pedidos" num={dinero(est.montoComprado)} sub={`de ${dinero(est.montoPedido)} pedidos`} color={ACENTO} />
            <Tile lbl="⏱️ Tiempo en atender" num={textoTiempo(est.minutosAtencion)} sub="Lo normal (mediana)" />
          </div>

          <Seccion titulo="De pedido a venta" sub="Cuántos pedidos llegan hasta la compra">
            {embudo.map((f, i) => (
              <div key={f.lbl} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                <span style={{ width: '130px', fontSize: '13px', color: '#374151' }}>{f.lbl}</span>
                <Barra valor={f.n} max={est.recibidos} color={i === 2 ? '#059669' : ACENTO} />
                <span style={{ width: '90px', textAlign: 'right', fontSize: '13px', fontWeight: 700, color: TINTA }}>
                  {f.n}{i > 0 && est.recibidos ? <span style={{ fontWeight: 400, color: SUAVE }}> · {Math.round((f.n / est.recibidos) * 100)}%</span> : ''}
                </span>
              </div>
            ))}
            {atendidosConResultado > 0 && (
              <div style={{ display: 'flex', height: '12px', borderRadius: '999px', overflow: 'hidden', gap: '2px', marginTop: '6px' }}
                role="img" aria-label="Resultado de los pedidos atendidos">
                {[...RESULTADOS, SIN_RESULTADO].map((r) => {
                  const n = r.clave ? est[r.clave] : est.sinResultado;
                  return n > 0 ? <div key={r.etiqueta} title={`${r.etiqueta}: ${n}`} style={{ flex: n, backgroundColor: r.clave ? r.color : '#D1D5DB' }} /> : null;
                })}
              </div>
            )}
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '8px', fontSize: '12px', color: SUAVE }}>
              {[...RESULTADOS, SIN_RESULTADO].map((r) => (
                <span key={r.etiqueta} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                  <span style={{ width: '10px', height: '10px', borderRadius: '3px', backgroundColor: r.clave ? r.color : '#D1D5DB' }} />
                  {r.etiqueta}: {r.clave ? est[r.clave] : est.sinResultado}
                </span>
              ))}
            </div>
          </Seccion>

          <Seccion titulo="¿De dónde llegan los pedidos?" sub="Por dónde entró la clienta al catálogo, y cuántos de esos compraron">
            {est.porOrigen.map((o) => (
              <div key={o.origen || 'sin'} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px', flexWrap: 'wrap' }}>
                <span style={{ width: '160px', fontSize: '13px', color: '#374151' }}>{o.icono} {o.etiqueta}</span>
                <Barra valor={o.pedidos} max={maxOrigen} />
                <span style={{ minWidth: '150px', fontSize: '13px', color: TINTA }}>
                  <b>{o.pedidos}</b> pedido(s) · <b style={{ color: '#047857' }}>{o.compro}</b> compraron <span style={{ color: SUAVE }}>({o.conversion}%)</span>
                </span>
              </div>
            ))}
            {est.porOrigen.some((o) => !o.origen) && (
              <p style={{ margin: '8px 0 0', fontSize: '12px', color: SUAVE }}>
                ❔ "Sin etiqueta" son pedidos de enlaces sin origen (casi siempre WhatsApp o un enlace copiado). Para saberlo exacto, comparte el catálogo con la etiqueta en <b>Compartir catálogo</b>. Instagram, Facebook y TikTok ya se detectan solos.
              </p>
            )}
          </Seccion>

          {est.motivos.length > 0 && (
            <Seccion titulo="¿Por qué no compraron?" sub="Lo que anotaste al marcar “No compró”">
              {est.motivos.map((m) => (
                <div key={m.clave} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                  <span style={{ width: '190px', fontSize: '13px', color: '#374151' }}>{m.etiqueta}</span>
                  <Barra valor={m.n} max={maxMotivo} color="#DC2626" />
                  <span style={{ width: '30px', textAlign: 'right', fontSize: '13px', fontWeight: 700 }}>{m.n}</span>
                </div>
              ))}
            </Seccion>
          )}
        </>
      )}

      <Seccion titulo="Pedidos por semana" sub="Últimas 8 semanas (no depende del período) · toca una semana para ver el detalle">
        <div style={{ position: 'relative' }} onMouseLeave={() => setSemanaActiva(null)}>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '6px', height: '120px', borderBottom: `1px solid ${LINEA}` }}>
            {semanas.map((s, i) => (
              <button key={i} onMouseEnter={() => setSemanaActiva(i)} onClick={() => setSemanaActiva(i)}
                aria-label={`Semana del ${fechaCorta(s.desde)}: ${s.pedidos} pedidos, ${s.compro} compraron`}
                style={{ flex: 1, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
                {/* Parte de abajo: compraron (fuerte). Arriba: el resto de pedidos (suave). 2px de separación. */}
                {s.pedidos - s.compro > 0 && (
                  <div style={{ height: `${((s.pedidos - s.compro) / maxSemana) * 100}%`, backgroundColor: ACENTO, opacity: semanaActiva === i ? 0.55 : 0.32, borderRadius: '4px 4px 0 0', marginBottom: s.compro ? '2px' : 0 }} />
                )}
                {s.compro > 0 && (
                  <div style={{ height: `${(s.compro / maxSemana) * 100}%`, backgroundColor: '#059669', borderRadius: s.pedidos - s.compro > 0 ? 0 : '4px 4px 0 0' }} />
                )}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: '6px', marginTop: '4px' }}>
            {semanas.map((s, i) => (
              <span key={i} style={{ flex: 1, textAlign: 'center', fontSize: '11px', color: s.actual ? TINTA : SUAVE, fontWeight: s.actual ? 700 : 400 }}>
                {s.actual ? 'esta' : fechaCorta(s.desde)}
              </span>
            ))}
          </div>
          {sa && (
            <div style={{ position: 'absolute', top: 0, left: `${((semanaActiva + 0.5) / semanas.length) * 100}%`, transform: `translateX(${semanaActiva > 3 ? '-100%' : '0'})`,
              backgroundColor: 'white', border: `1px solid ${LINEA}`, borderRadius: '10px', padding: '8px 10px', boxShadow: '0 6px 18px rgba(30,20,60,0.12)', fontSize: '12px', whiteSpace: 'nowrap', pointerEvents: 'none' }}>
              <b>Semana del {fechaCorta(sa.desde)}</b><br />{sa.pedidos} pedido(s) · {sa.compro} compraron
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: '12px', marginTop: '8px', fontSize: '12px', color: SUAVE }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}><span style={{ width: '10px', height: '10px', borderRadius: '3px', backgroundColor: '#059669' }} />Compraron</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}><span style={{ width: '10px', height: '10px', borderRadius: '3px', backgroundColor: ACENTO, opacity: 0.32 }} />Resto de pedidos</span>
        </div>
      </Seccion>
    </div>
  );
}
